import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { gql, useLazyQuery } from "@apollo/client";
import Link from "next/link";
import FadeIn from "react-fade-in";
import { useRouter } from "next/router";
import {
  Home,
  FileText,
  RefreshCw,
  PenTool,
  Bell,
  Settings,
  HelpCircle,
  Power,
  FileUser,
  BadgeDollarSign,
  UserPlus,
} from "lucide-react";

import { getNotificationsByUserId } from "@src/graphql/queries";
import { userIsCandidate, userIsEnterprise } from "@src/helpers";
import { useAuth } from "@src/auth";
import { useAppState } from "@src/state";

import styles from "./style.module.css";

const getIconForMenuItem = (name) => {
  const iconProps = { size: 20, className: styles.icon };

  switch (name) {
    case "Home":
      return <Home {...iconProps} />;
    case "Applications":
      return <FileText {...iconProps} />;
    case "Auto-Apply":
      return <RefreshCw {...iconProps} />;
    case "Cover Letter AI":
      return <PenTool {...iconProps} />;
    case "Resume AI":
      return <FileUser {...iconProps} />;
    case "Notifications":
      return <Bell {...iconProps} />;
    case "Referral Program":
      return <UserPlus {...iconProps} />;
    case "Manage Plan":
      return <BadgeDollarSign {...iconProps} />;
    case "Preferences":
      return <Settings {...iconProps} />;
    case "Help":
      return <HelpCircle {...iconProps} />;
    case "Sign out":
      return <Power {...iconProps} />;
    default:
      return <Home {...iconProps} />;
  }
};

export default function MobileMenu() {
  const { user, signOutUser } = useAuth();
  const { state, dispatch } = useAppState();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 820px)" });
  const [hideRows, setHideRows] = useState(true);
  const userId = useAuth()?.user?.username;
  const [attempts, setAttempts] = useState(3);

  const [getUserData, { data: userData, loading: userLoading }] = useLazyQuery(
    gql(getUserById),
    {
      variables: { userId: user?.username },
      fetchPolicy: "network-only",
    }
  );

  const [loadNotifications, { called, loading, data }] = useLazyQuery(
    gql(getNotificationsByUserId),
    { variables: { userId, last: 10 } }
  );

  useEffect(() => {
    if (user && attempts > 0 && !userData?.getUserById?.onboardingCompleted) {
      console.log("attempts:", attempts);
      console.log("user data: ", userData);
      getUserData();
      setTimeout(() => {
        setAttempts(attempts - 1);
      }, 600);
    }
  }, [attempts]);

  useEffect(() => {
    if (user && !called && userData?.getUserById?.onboardingCompleted) {
      loadNotifications();
    }
  }, [user, userData]);

  let notifs = [];

  if (called && data?.getNotificationsByUserId?.edges) {
    const edges = data?.getNotificationsByUserId?.edges ?? [];
    notifs = edges.map((edge) => edge.node);
  }

  let unread = false;

  if (notifs?.length) {
    notifs.forEach((item) => {
      if (item?.read === false) {
        unread = true;
      }
    });
  }

  useEffect(() => {
    if (state.mobileMenu) {
      setTimeout(() => {
        setHideRows(false);
      }, 100);
    } else if (!state.mobileMenu) {
      setHideRows(true);
    }
  }, [state.mobileMenu]);

  const closeMenu = () => {
    dispatch({ type: "close_mobile_menu" });
  };

  const signout = () => {
    closeMenu();
    signOutUser();
  };

  let menuStyle = {
    width: "0",
    opacity: 0,
  };

  if (state?.mobileMenu) {
    menuStyle = {
      width: "100%",
      opacity: 1,
    };
  }

  if (!isTabletOrMobile && state.mobileMenu) {
    setTimeout(() => dispatch({ type: "close_mobile_menu" }), 0);
    return null;
  }

  const menuItems = () => {
    if (userData?.getUserById?.onboardingCompleted === false) {
      return [
        {
          name: "Sign out",
          onClick: signout,
        },
      ];
    }

    if (!user) {
      return [
        {
          name: "Home",
          href: "/",
        },
        {
          name: "Pricing",
          href: "/#pricing",
        },
        {
          name: "Sign In",
          href: "/signin",
        },
        {
          name: "Create Account",
          href: "/signup",
        },
      ];
    } else if (userIsCandidate(user)) {
      if (!state?.sidebar) {
        return [
          {
            name: "Auto-Apply",
            href: "/candidate/dashboard/auto",
          },   
          {
            name: "Notifications",
            href: "/candidate/dashboard/notifications",
            notifications: unread,
          },   
          {
            name: "Sign out",
            onClick: signout,
          }                                
        ]
      }
      return [
        {
          name: "Auto-Apply",
          href: "/candidate/dashboard/auto",
        },        
        {
          name: "Applications",
          href: "/candidate/dashboard/applications",
        },
        {
          name: "Notifications",
          href: "/candidate/dashboard/notifications",
          notifications: unread,
        },
        {
          name: "Cover Letter AI",
          href: "/candidate/dashboard/coverletter",
        },
        {
          name: "Resume AI",
          href: "/candidate/dashboard/resume",
        },
        {
          name: "Referral Program",
          href: "/candidate/dashboard/referral",
        },
        {
          name: "Manage Plan",
          href: "/candidate/dashboard/manage",
        },
        {
          name: "Preferences",
          href: "/candidate/dashboard/preferences",
        },
        {
          name: "Help",
          href: "/help",
        },
        {
          name: "Sign out",
          onClick: signout,
        },
      ];
    } else if (userIsEnterprise(user)) {
      return [
        {
          name: "Job Search",
          href: "/",
        },
        {
          name: "Candidates",
          href: "/employer/dashboard/candidates",
        },
        {
          name: "Job Postings",
          href: "/employer/dashboard/jobs",
        },
        {
          name: "Notifications",
          href: "/employer/dashboard/notifications",
          notifications: unread,
        },
        {
          name: "Preferences",
          href: "/employer/dashboard/preferences",
        },
        {
          name: "Sign out",
          onClick: signout,
        },
      ];
    }
  };

  function MenuItem({ item, closeMenu }) {
    const router = useRouter();
    const pathName = router.pathname;
    const isActive = item.href === pathName;
    const icon = getIconForMenuItem(item.name);
  
    // Inline styles for the Referral Program
    const referralProgramStyle =
      item.name === "Referral Program"
        ? {
            background: "linear-gradient(to right, #34d399, #22c55e)",
            borderRadius: "12px",
            padding: "10px",
            boxShadow: "0 0 15px rgba(16, 185, 129, 0.5)",
            color: "#ffffff",
            transition: "transform 0.3s, box-shadow 0.3s",
            transform: "scale(1)",
          }
        : {};
  
    // Hover effect (applies only to Referral Program)
    const onMouseEnter = (e) => {
      if (item.name === "Referral Program") {
        e.currentTarget.style.boxShadow = "0 0 20px rgba(16, 185, 129, 0.7)";
      }
    };
  
    const onMouseLeave = (e) => {
      if (item.name === "Referral Program") {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.boxShadow = "0 0 15px rgba(16, 185, 129, 0.5)";
      }
    };
  
    if (item.href) {
      return (
        <span onClick={closeMenu}>
          <Link href={item.href} passHref>
            <div
              className={`${styles.menuItem} ${isActive ? styles.active : ""}`}
              style={referralProgramStyle}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              <div className={styles.itemContent}>
                <div className={styles.itemLeft}>
                  {icon}
                  <span className={styles.rowName}>{item.name}</span>
                </div>
                {item.notifications && <div className={styles.unreadDot} />}
              </div>
            </div>
          </Link>
        </span>
      );
    }
  
    return (
      <div
        className={`${styles.menuItem} ${isActive ? styles.active : ""}`}
        style={referralProgramStyle}
        onClick={item.onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className={styles.itemContent}>
          <div className={styles.itemLeft}>
            {icon}
            <span className={styles.rowName}>{item.name}</span>
          </div>
        </div>
      </div>
    );
  }

  function MenuSection({ title, items, closeMenu }) {
    if (!items?.length) return null;

    return (
      <div className={styles.section}>
        {title && <div className={styles.sectionTitle}>{title}</div>}
        {items.map((item) => (
          <MenuItem key={item.name} item={item} closeMenu={closeMenu} />
        ))}
      </div>
    );
  }

  const items = menuItems();

  return (
    <div className={styles.container} style={menuStyle}>
      <div className={styles.menuContent}>
        {!hideRows && (
          <FadeIn className={styles.menuList}>
            {userIsCandidate(user) ? (
              <>
                <MenuSection
                  title="Main"
                  items={items.slice(0, 3)}
                  closeMenu={closeMenu}
                />
                <MenuSection
                  title="Tools"
                  items={items.slice(3, 5)}
                  closeMenu={closeMenu}
                />
                <MenuSection
                  title="Settings & Support"
                  items={items.slice(5)}
                  closeMenu={closeMenu}
                />
              </>
            ) : (
              <MenuSection items={items} closeMenu={closeMenu} />
            )}
          </FadeIn>
        )}

        <div className={styles.footer}>
          Need assistance? Send an email to support@lifeshack.com
        </div>
      </div>
    </div>
  );
}

export const getUserById = /* GraphQL */ `
  query GetUserById($userId: String!) {
    getUserById(userId: $userId) {
      onboardingCompleted
    }
  }
`;
